export const DEFAULT_BANNER_EMOJI = '📢';
export const DEFAULT_BANNER_BACKGROUND_COLOR = '#FF6B35';
export const DEFAULT_BANNER_TEXT_COLOR = '#FFFFFF';

type BannerDesignerInput = {
  title: string;
  description?: string | null;
  emoji?: string | null;
  backgroundColor: string;
  textColor: string;
};

const SVG_WIDTH = 1200;
const SVG_HEIGHT = 400;

function escapeXml(value: string) {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&apos;');
}

function wrapBannerText(value: string, maxCharsPerLine: number, maxLines: number) {
  const words = value.trim().split(/\s+/).filter(Boolean);

  if (words.length === 0) {
    return [];
  }

  const lines: string[] = [];
  let currentLine = '';

  for (const word of words) {
    const nextLine = currentLine ? `${currentLine} ${word}` : word;

    if (nextLine.length <= maxCharsPerLine) {
      currentLine = nextLine;
      continue;
    }

    if (currentLine) {
      lines.push(currentLine);
    }

    currentLine = word;

    if (lines.length === maxLines - 1) {
      break;
    }
  }

  if (currentLine && lines.length < maxLines) {
    lines.push(currentLine);
  }

  if (lines.length > maxLines) {
    return lines.slice(0, maxLines);
  }

  const originalText = value.trim();
  const rebuiltText = lines.join(' ');

  if (rebuiltText.length < originalText.length && lines.length > 0) {
    const lastLineIndex = lines.length - 1;
    const shortened = lines[lastLineIndex].slice(0, Math.max(0, maxCharsPerLine - 1)).trimEnd();
    lines[lastLineIndex] = `${shortened}…`;
  }

  return lines;
}

function renderTextBlock(
  lines: string[],
  {
    x,
    lineHeight,
  }: {
    x: number;
    lineHeight: number;
  }
) {
  return lines
    .map(
      (line, index) =>
        `<tspan x="${x}" dy="${index === 0 ? 0 : lineHeight}">${escapeXml(line)}</tspan>`
    )
    .join('');
}

export function buildBannerSvgDataUrl(input: BannerDesignerInput) {
  const title = input.title.trim() || 'Baslik';
  const description = input.description?.trim() || 'Aciklama';
  const emoji = input.emoji?.trim() || DEFAULT_BANNER_EMOJI;
  const backgroundColor = input.backgroundColor || DEFAULT_BANNER_BACKGROUND_COLOR;
  const textColor = input.textColor || DEFAULT_BANNER_TEXT_COLOR;
  const titleLines = wrapBannerText(title, 24, 2);
  const descriptionLines = wrapBannerText(description, 34, 2);

  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${SVG_WIDTH}" height="${SVG_HEIGHT}" viewBox="0 0 ${SVG_WIDTH} ${SVG_HEIGHT}" fill="none">
      <rect width="${SVG_WIDTH}" height="${SVG_HEIGHT}" rx="40" fill="${backgroundColor}" />
      <circle cx="1040" cy="70" r="150" fill="${textColor}" fill-opacity="0.12" />
      <circle cx="1130" cy="335" r="120" fill="${textColor}" fill-opacity="0.08" />
      <rect x="70" y="78" width="8" height="244" rx="4" fill="${textColor}" fill-opacity="0.5" />

      <text
        x="110"
        y="140"
        fill="${textColor}"
        font-family="Inter, Arial, sans-serif"
        font-size="60"
        font-weight="700"
      >${renderTextBlock(titleLines, { x: 110, lineHeight: 72 })}</text>

      <text
        x="110"
        y="252"
        fill="${textColor}"
        fill-opacity="0.92"
        font-family="Inter, Arial, sans-serif"
        font-size="30"
        font-weight="500"
      >${renderTextBlock(descriptionLines, { x: 110, lineHeight: 40 })}</text>

      <text
        x="980"
        y="230"
        fill="${textColor}"
        font-family="Apple Color Emoji, Segoe UI Emoji, Noto Color Emoji, sans-serif"
        font-size="150"
        text-anchor="middle"
      >${escapeXml(emoji)}</text>
    </svg>
  `.trim();

  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}
